import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Link as ScrollLink, scroller } from 'react-scroll';
import { useDispatch } from 'react-redux';

import { useQuizModal, useBuildBundleModal } from '@components/common/presentational/modal/common';
import { packAndTrack } from '@v2/services/analytics.service';

import Clickable from '@v2/components/common/presentational/clickables/default';
import AgeBundles from '@v2/components/pages/shop/partial/age-bundles';
import VarietyPacks from '@v2/components/pages/shop/partial/variety-packs';
import Packs from '@pages/shop/partial/packs';
import Collaborations from '@pages/shop/partial/collaborations';
import UpcomingTab from '@pages/shop/partial/upcoming';
import Personalized from '@pages/shop/partial/personalized';
import Filter from '@pages/shop/partial/filter';
import { ContentLoader } from '@components/common/presentational/loader/loader';

import v2WrapperStyles from '@styles/wrapper';
import styles from '@v2/components/pages/shop/shop.styles';

import largeArrowRight from '@img/shop/large_arrow_right.svg';

import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { getScrollPosition, removeScrollPosition } from '@components/common/helpers/scroll-position';
import { IProduct } from '@v2/interfaces';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { useIsLogged } from '@app/store/rtk.user.selectors';
import { useIsGlobalSubscribed } from '@v2/store/cart.selectors';
import Checkbox from '@v2/components/common/presentational/checkbox/checkbox';
import { useIsMobile } from '@v2/components/common/helpers/screen-check';
import { useHistoryWithReferrer } from '@v2/utils/history';
import { hideGeneralModal } from '@v2/store/rtk.modals.store';

interface IBundleCategory {
    id: string,
    title: string,
    description?: string,
    ref: React.MutableRefObject<any>,
    component: React.FunctionComponent<any>,
    disabled: boolean,
    loaded: boolean,
    hasProducts?: boolean,
    price?: number,
    pricePerMeal?: number,
    products?: object,
    offset?: number,
    combine?: string[],
}

export function useMenuItems(): IBundleCategory[] {
    const isLogged = useIsLogged();
    const categories = useEntity('categories');
    const pouchlike = [];
    const packs = {
        pouch: useEntity('pouchpacks') || {},
        smoothie: useEntity('smoothiepacks') || {},
        bar: useEntity('barpacks') || {},
        bonebrothpouch: useEntity('bonebrothpouchpacks') || {},
    }
    const products = {
        pouch: useEntity('pouches') || {},
        bar: useEntity('bars') || {},
        smoothie: useEntity('smoothies') || {},
        bonebrothpouch: useEntity('bonebrothpouches') || {},
        collaborations: useEntity('collaborations') || {},
    }

    // if (packs.bonebrothpouch.length > 0) {
    //     for (const key in packs.bonebrothpouch) {
    //         pouchlike.push(packs.bonebrothpouch[key])
    //     }
    // }

    if (packs.pouch.length > 0) {
        for (const key in packs.pouch) {
            pouchlike.push(packs.pouch[key])
        }
    }

    const availableCategories: { [key: string]: IBundleCategory } = {
        'pouches': {
            id: 'pouch',
            title: 'Purees',
            description: `Packed with 16 brain-supporting nutrients—8x more than the average pouch—these purees are perfect from first bites to toddler snacks on the go.`,
            ref: useRef(null),
            component: Packs,
            disabled: false,
            loaded: true,
            hasProducts: true,
            products: pouchlike,
            pricePerMeal: Object.keys(products.pouch).length > 0
                ? (Object.values(products.pouch).shift() as IProduct).priceSubscribed
                : null,
            offset: -140,
            combine: ['filter'],
        },
        'bars': {
            id: 'bar',
            title: 'Smart Bars',
            description: `The perfect go-to snack: 16 brain-supporting nutrients, 0g added sugar, and 3g of protein—all in one smart bar.`,
            ref: useRef(null),
            component: Packs,
            disabled: false,
            loaded: true,
            hasProducts: true,
            products: packs.bar,
            pricePerMeal: Object.keys(products.bar).length > 0
                ? (Object.values(products.bar).shift() as IProduct).priceSubscribed
                : null,
            offset: -140,
        },
        'bonebrothpouches': {
            id: 'bonebrothpouch',
            title: 'Bone Broth Protein Purees',
            description: `With 5g of protein sourced from organic, free range bone broth and superfood legumes and veggies,
                these purees deliver 16 brain-supporting nutrients to fuel growing minds and bodies.`,
            ref: useRef(null),
            component: Packs,
            disabled: false,
            loaded: true,
            hasProducts: true,
            products: packs.bonebrothpouch,
            pricePerMeal: Object.keys(products.bonebrothpouch).length > 0
                ? (Object.values(products.bonebrothpouch).shift() as IProduct).priceSubscribed
                : null,
            offset: -140,
        },
        'smoothies': {
            id: 'smoothie',
            title: 'Smoothie-Style Purees',
            description: `These organic, lactose-free and dairy-free smoothie pouches with 16 brain supporting nutrients blend nutritious veggies
                and delicious fruits for a naturally sweet snack that kids of all ages love.`,
            ref: useRef(null),
            component: Packs,
            disabled: false,
            loaded: true,
            hasProducts: true,
            products: packs.smoothie,
            pricePerMeal: Object.keys(products.smoothie).length > 0
                ? (Object.values(products.smoothie).shift() as IProduct).priceSubscribed
                : null,
            offset: -140,
        },
        /*
        'peppa': {
            id: 'peppa',
            title: 'Cerebelly X Peppa Pig',
            description: `We've partnered up with Peppa Pig and created fun new packaging for her favorite treats!
                          Who knew eating healthy could be so much fun?`,
            ref: useRef(null),
            component: Packs,
            disabled: true,
            products: packs.peppa,
            pricePerMeal: prices ? prices.pouches : null,
        },
        */
    };

    const categoryData: IBundleCategory[] = [
        {
            id: 'personalized',
            title: 'Made for You',
            description: `Begin your adventure with smarter nutrition! Take the quiz to receive our recommended plan or build a bundle below.`,
            ref: useRef(null),
            component: Personalized,
            disabled: false,
            loaded: false,
            pricePerMeal: Object.keys(products.pouch).length > 0
                ? (Object.values(products.pouch).shift() as IProduct).priceSubscribed
                : null,
            combine: ['picker'],
        },
        {
            id: 'bundles',
            title: 'Bundles',
            description: `Expertly created to provide the optimal nutrition for each stage of brain development.
                          Select an age to learn more about what your little one's brain is up to right now!`,
            ref: useRef(null),
            component: AgeBundles,
            disabled: false,
            loaded: true,
            pricePerMeal: Object.keys(products.pouch).length > 0
                ? (Object.values(products.pouch).shift() as IProduct).priceSubscribed
                : null,
        },
        {
            id: 'variety-packs',
            title: 'Variety Packs',
            description: `Let your little one be in charge with packs that test their taste buds!
                          These assortments of Cerebelly faves prove that superior nutrition and taste can go hand in hand.`,
            ref: useRef(null),
            component: VarietyPacks,
            disabled: false,
            loaded: true,
            pricePerMeal: Object.keys(products.pouch).length > 0
                ? (Object.values(products.pouch).shift() as IProduct).priceSubscribed
                : null,
        },
        {
            id: 'filter',
            title: null,
            ref: useRef(null),
            component: Filter,
            disabled: false,
            loaded: true,
        },

    ];

    for (let key in categories) {
        if (availableCategories[categories[key].slug]) {
            categoryData.push(availableCategories[categories[key].slug]);
        }
    }

    categoryData.push({
        id: 'the-dough-project',
        title: 'The Dough Project',
        description: `Smart nutrients meet all natural, plant-based playdough. One-time purchase only.`,
        ref: useRef(null),
        component: Collaborations,
        disabled: false,
        loaded: true,
        hasProducts: true,
        products: products.collaborations,
        pricePerMeal: Object.keys(products.collaborations).length > 0
            ? (Object.values(products.collaborations).shift() as IProduct).priceSubscribed
            : null,
    });

    categoryData.push({
        id: 'upcoming',
        title: null,
        ref: useRef(null),
        component: UpcomingTab,
        disabled: isLogged,
        loaded: false,
    });

    return useMemo(() => categoryData, [categories, isLogged]);
}

function useAutoScrolling(categories: any, loading: boolean, setActiveTab: Function) {
    // this is fucking rediculous
    useScrollPosition(() => {
        const stats: any = {};
        categories.forEach((category: any) => {
            if (!category.disabled && (!category.loaded || !loading)) {
                const element = window.document.getElementsByClassName(`element-${category.id}`)[0];

                stats[category.id] = {
                    top: element.getBoundingClientRect().top,
                    bottom: element.getBoundingClientRect().bottom,
                    percentage: 0
                }
            }
        });

        categories.forEach((category: any) => {
            if (!category.disabled && (!category.loaded || !loading) && category.combine) {
                category.combine.forEach((id: number) => {
                    if (stats[id] && stats[category.id] && stats[id].top < stats[category.id].top) {
                        stats[category.id].top = stats[id].top;
                    }

                    if (stats[id] && stats[category.id] && stats[id].bottom > stats[category.id].bottom) {
                        stats[category.id].bottom = stats[id].bottom;
                    }

                    delete stats[id];
                });
            }
        });

        let max = 0;
        let maxId = 'personalized';
        Object.keys(stats).forEach(id => {
            const bottom = stats[id].bottom;
            const top = stats[id].top;

            //const divposition = document.getElementById('swap_products').getBoundingClientRect();
            //const currentScrolled = divposition.top + divposition.height < 0;

            const height = window.innerHeight;

            stats[id].percentage =
                bottom < 0
                    ? 0
                    : (top > height
                        ? 0
                        : (Math.min(bottom, height) - Math.max(top, 0)) / height);

            if (stats[id].percentage > max) {
                max = stats[id].percentage;
                maxId = id;
            }
        });

        setActiveTab(maxId);
    }, null, null, false, 200);
}

function SideMenu({ loading, activeTab, subscribed, setSubscribed }: { loading: boolean, activeTab: string, subscribed: boolean, setSubscribed: Function }) {
    const [showQuiz] = useQuizModal();
    const [showBuilder] = useBuildBundleModal();
    const categories = useMenuItems();
    const ref = useRef(null);
    const ref2 = useRef(null);
    const isMobile = useIsMobile(900);

    const eventAction = (name: string, section?: string) => {
        packAndTrack(
            `${name} Clicked`,
            [{ 'view': 'Shop', 'section': section ? section : 'Shop' }]);
    }

    useEffect(() => {
        if (ref.current?.scrollTo) {
            categories.forEach((category: any, index: number) => {
                if (category.id === activeTab) {
                    ref.current.scrollTo(index * 70, 0);
                    return;
                }
            });
        }
    }, [activeTab]);

    useEffect(() => {
        if (!isMobile) {
            return;
        }

        let prevScrollpos = window.pageYOffset;

        window.onscroll = function () {
            let currentScrollPos = window.pageYOffset;

            if (ref2.current?.classList) {
                if (prevScrollpos > currentScrollPos) {
                    ref2.current.classList.remove('boxmenuScroll');
                } else {
                    ref2.current.classList.add('boxmenuScroll');
                }
            }

            prevScrollpos = window.pageYOffset;
        }
    }, []);

    return (
        <div className={`boxmenuContainer`} ref={ref2}>
            <span className={`subscribed ${subscribed ? 'active' : 'inactive'} mobile`}>
                <Checkbox
                    name={'subscribed'}
                    size={'SMALL'}
                    type={'SWITCH'}
                    onChange={() => setSubscribed(!subscribed)}
                    checked={subscribed}
                    useParentState={true}
                >
                    Subscribe & Save 15%
                    <span className="anytime">
                        Conveniently recharged. Cancel anytime.
                    </span>
                </Checkbox>
            </span>
            <div ref={ref} className="sidebar">
                <div className="menu">
                    {
                        categories.map(category => {
                            return (!category.disabled
                                && (!category.loaded || !loading)
                                && category.title
                                && (!category.hasProducts
                                    || (category.products && Object.keys(category.products).length))) ?
                                <ScrollLink
                                    key={category.id}
                                    className={`menu-item noselect menu-${category.id}`}
                                    to={category.id}
                                    spy={true}
                                    offset={category.offset ? category.offset : -90}
                                    smooth={true}
                                    onClick={() => eventAction(category.title, 'Sidebar')}
                                    duration={500}>
                                    <span className={activeTab === category.id ? 'active' : null}>
                                        {category.title}
                                    </span>
                                </ScrollLink> : null
                        })
                    }
                    <ContentLoader
                        loading={loading}
                        mobileDots={true} />
                    <Clickable
                        className="menu-item standalone-item take-the-quiz"
                        onClick={() => {
                            showQuiz();
                            eventAction('Take Survey');
                        }}>
                        Take the quiz
                        <img src={largeArrowRight} alt="Arrow right" />
                    </Clickable>
                    <Clickable
                        className="menu-item standalone-item build-a-bundle"
                        onClick={() => {
                            showBuilder();
                            eventAction('Build Box');
                        }}>
                        Build a bundle
                        <img src={largeArrowRight} alt="Arrow right" />
                    </Clickable>
                    <Link
                        className="menu-item standalone-item gift-cards"
                        to="/gift">
                        Order a gift card
                        <img src={largeArrowRight} alt="Arrow right" />
                    </Link>
                    <span className={`subscribed ${subscribed ? 'active' : 'inactive'} desktop`} style={{marginBottom: 20}}>
                        <Checkbox
                            name={'subscribed'}
                            size={'SMALL'}
                            type={'SWITCH'}
                            onChange={() => setSubscribed(!subscribed)}
                            checked={subscribed}
                            useParentState={true}
                        >
                            Subscribe & Save 15%
                            <span className="anytime">
                                Conveniently recharged. Cancel anytime.
                            </span>
                        </Checkbox>
                    </span>
                </div>
            </div>
        </div>
    )
}

export function ProductListing({ loading, subscribed, isSwapping }: { loading: boolean, subscribed?: boolean, isSwapping?: boolean }) {
    const query = new URLSearchParams(window.location.search);

    const [filterBy, setFilterBy] = useState(null);
    const [filterAge, setFilterAge] = useState(null);
    const [scrolled, setScrolled] = useState(false);

    const ageBundlesEntities = useEntity('ageBundles') || null;
    const categories = useMenuItems();

    useEffect(() => {
        const bundleParam = query.get('bundle');

        if (bundleParam === null || !ageBundlesEntities || scrolled) {
            return;
        }

        setTimeout(() => {
            const bundlesElement = (window as any).document.getElementById('bundles');

            if (query.get('bundle') && !scrolled && bundlesElement) {
                scroller.scrollTo('bundles', { offset: -90 });
                setScrolled(true);
            }
        }, 500);

        Object.values(ageBundlesEntities).forEach((product: any, index: number) => {
            if (bundleParam === product.slug) {
                setFilterAge(index);
                return;
            }
        });
    }, [ageBundlesEntities]);

    return (
        <>
            {
                categories.map((category, key) => {
                    return (!category.disabled
                        && (!category.loaded || !loading)) ?
                        <div id={`${isSwapping ? 'swap-' : ''}${category.id}`}
                            className={`element-${category.id}`}
                            ref={category.ref}
                            key={key}>
                            {React.createElement(
                                category.component,
                                {
                                    loading: loading,
                                    category: category,
                                    filterBy: filterBy,
                                    filterAge: filterAge,
                                    setFilterBy: setFilterBy,
                                    setFilterAge: setFilterAge,
                                    subscribed: subscribed,
                                }
                            )}
                        </div> : null
                })
            }
        </>
    )
}

export default function ProductsBody({ loading }: { loading: boolean }) {
    const url = window.location.href as any;
    const index = url.indexOf("#");
    const hash = index !== -1 ? url.substring(index + 1) : null;
    const lastScrollPosition = getScrollPosition('shop');
    const globalSubscribed = useIsGlobalSubscribed() as boolean;
    const history = useHistoryWithReferrer();
    const isModalRoutePushed = useRef(false);

    const categories = useMenuItems();
    const [activeTab, setActiveTab] = useState('personalized');
    const [subscribed, setSubscribed] = useState(globalSubscribed);
    const dispatch = useDispatch();

    function scrollToCategory(catId: string) {
        categories.forEach(category => {
            if (category.id === catId) {
                packAndTrack(
                    `${category.title} Section Landed`,
                    [{ 'view': 'Shop', 'section': 'Shop' }]);
                scroller.scrollTo(category.id, { offset: category.offset ? category.offset : -90 })
                return;
            }
        });

        return;
    }

    useEffect(() => {
        if (!lastScrollPosition || !url.includes('/shop')) {
            return;
        }

        window.scrollTo(0, Number(lastScrollPosition));
        removeScrollPosition('shop');
    }, [url]);

    useEffect(() => {
        if(!hash) {
            return;
        }
        setTimeout(() => {
            scrollToCategory(hash);
        }, 500);
    }, [hash, categories])

    useEffect(() => {
        if (!hash) {
            return;
        }

        if((hash === 'quiz' || hash === 'builder') && isModalRoutePushed.current) {
            isModalRoutePushed.current = false;
            window.history.replaceState("", document.title, window.location.pathname);
            dispatch(hideGeneralModal());
            return;
        }

        setTimeout(() => {
            switch(hash) {
                case 'quiz':
                    isModalRoutePushed.current = true;
                    history.pushWithReferrer('/survey');
                    break;
                case 'builder':
                    isModalRoutePushed.current = true;
                    history.pushWithReferrer('/box-builder');
                    break;
            }
        }, 500);
    }, [hash]);

    useAutoScrolling(categories, loading, setActiveTab);

    return (
        <div className={[v2WrapperStyles.wrapper, styles.container].join(' ')}>
            <SideMenu loading={loading} activeTab={activeTab} subscribed={subscribed} setSubscribed={setSubscribed} />
            <div className="mainContainer">
                <div className="pageContainer">
                    <div className="container">
                        <ProductListing loading={loading} subscribed={subscribed} />
                    </div>
                </div>
            </div>
        </div>
    );
}
